import hsk from "../data/charactersList.json";
import { getCardId } from "./cardId";
import {
  CardStatRecord,
  SETTINGS_ID,
  db,
  TestAnswerRecord,
  TestSessionRecord
} from "./db";
import {
  NeedsWorkItem,
  overallAccuracyPercent,
  rankNeedsWork,
  sessionPercents
} from "./progress";
import { AnswerRecord, Details, HskEntry, SessionState } from "./types";

const LEGACY_STORAGE_KEY = "mandarin-flashcard-session";

export type { AnswerRecord, SessionState } from "./types";

export type SavedTestResult = {
  cards: HskEntry[];
  answers: AnswerRecord[];
  sessionId?: string;
  finishedAt?: number;
  filteredLevels?: number[];
  shuffled?: boolean;
};

export type ProgressOverview = {
  testCount: number;
  accuracyPercent: number | null;
  cardsSeen: number;
  scores: { finishedAt: number; percent: number }[];
  needsWork: NeedsWorkItem[];
  history: TestSessionRecord[];
};

export const defaultDetailsOptions: Details = {
  front: { charSimp: true, charTrad: true, pinyin: false, meaning: false },
  back: { charSimp: false, charTrad: false, pinyin: true, meaning: true }
};

export const mobileDefaults: Partial<SessionState> = {
  filteredLevels: [1],
  cardAmount: 20,
  chunkLength: 1,
  detailsOptions: defaultDetailsOptions
};

export const getStudyChunkDefault = (isMobile: boolean) => (isMobile ? 10 : 20);

export const getGridChunkDefault = (isMobile: boolean) => (isMobile ? 30 : 0);

const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const readLegacySession = (): Partial<SessionState> | null => {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as Partial<SessionState>;
  } catch {
    return null;
  }
};

const clearLegacySession = () => {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore quota / access errors
  }
};

const toSession = (record: SessionState & { id: string }): SessionState => {
  const { id: _id, ...session } = record;
  return session;
};

export const loadSession = async (): Promise<Partial<SessionState> | null> => {
  try {
    const saved = await db.settings.get(SETTINGS_ID);
    if (saved) {
      return toSession(saved);
    }

    const legacy = readLegacySession();
    if (!legacy) {
      return null;
    }

    await db.settings.put({ id: SETTINGS_ID, ...legacy } as SessionState & { id: string });
    clearLegacySession();
    return legacy;
  } catch {
    return readLegacySession();
  }
};

export const saveSession = async (state: SessionState) => {
  try {
    await db.settings.put({ id: SETTINGS_ID, ...state });
  } catch {
    // ignore quota errors
  }
};

const nextStat = (
  existing: CardStatRecord | undefined,
  answer: boolean,
  now: number
): CardStatRecord => ({
  cardId: existing?.cardId ?? "",
  timesCorrect: (existing?.timesCorrect ?? 0) + (answer ? 1 : 0),
  timesIncorrect: (existing?.timesIncorrect ?? 0) + (answer ? 0 : 1),
  lastResult: answer,
  lastSeenAt: now,
  correctStreak: answer ? (existing?.correctStreak ?? 0) + 1 : 0
});

export const recordCardResult = async (cardId: string, answer: boolean) => {
  const now = Date.now();
  await db.transaction("rw", db.cardStats, async () => {
    const existing = await db.cardStats.get(cardId);
    await db.cardStats.put({ ...nextStat(existing, answer, now), cardId });
  });
};

export const recordCompletedTest = async ({
  cards,
  answers,
  filteredLevels,
  cardAmount,
  shuffled
}: {
  cards: HskEntry[];
  answers: AnswerRecord[];
  filteredLevels: number[];
  cardAmount: number;
  shuffled: boolean;
}): Promise<SavedTestResult> => {
  const answersByOrder = new Map(answers.map((entry) => [entry.order, entry.answer]));
  let correctCount = 0;
  let incorrectCount = 0;
  let unansweredCount = 0;

  const rows = cards.map((card, position) => {
    const answer = answersByOrder.has(card.order) ? answersByOrder.get(card.order)! : null;
    if (answer === true) {
      correctCount += 1;
    } else if (answer === false) {
      incorrectCount += 1;
    } else {
      unansweredCount += 1;
    }
    return { card, position, answer };
  });

  const session: TestSessionRecord = {
    id: newId(),
    finishedAt: Date.now(),
    filteredLevels,
    cardAmount,
    shuffled,
    correctCount,
    incorrectCount,
    unansweredCount,
    totalCount: cards.length
  };

  const answerRows: TestAnswerRecord[] = rows.map(({ card, position, answer }) => ({
    sessionId: session.id,
    cardId: getCardId(card),
    order: card.order,
    position,
    answer
  }));

  await db.transaction("rw", db.testSessions, db.testAnswers, db.cardStats, async () => {
    await db.testSessions.add(session);
    if (answerRows.length > 0) {
      await db.testAnswers.bulkAdd(answerRows);
    }

    const now = session.finishedAt;
    for (const { card, answer } of rows) {
      if (answer !== true && answer !== false) {
        continue;
      }
      const cardId = getCardId(card);
      const existing = await db.cardStats.get(cardId);
      await db.cardStats.put({ ...nextStat(existing, answer, now), cardId });
    }
  });

  return {
    sessionId: session.id,
    finishedAt: session.finishedAt,
    filteredLevels: session.filteredLevels,
    shuffled: session.shuffled,
    cards,
    answers
  };
};

let cardsByOrderCache: Map<number, HskEntry> | null = null;
let cardsByIdCache: Map<string, HskEntry> | null = null;

const getCardsByOrder = () => {
  if (!cardsByOrderCache) {
    cardsByOrderCache = new Map((hsk as HskEntry[]).map((entry) => [entry.order, entry]));
  }
  return cardsByOrderCache;
};

const getCardsById = () => {
  if (!cardsByIdCache) {
    cardsByIdCache = new Map((hsk as HskEntry[]).map((entry) => [getCardId(entry), entry]));
  }
  return cardsByIdCache;
};

const hydrateSession = async (session: TestSessionRecord): Promise<SavedTestResult | null> => {
  const answerRows = await db.testAnswers.where("sessionId").equals(session.id).sortBy("position");
  const cardsByOrder = getCardsByOrder();
  const cards: HskEntry[] = [];
  const answers: AnswerRecord[] = [];

  answerRows.forEach((row) => {
    const card = cardsByOrder.get(row.order);
    if (!card) {
      return;
    }
    cards.push(card);
    answers.push({ order: row.order, answer: row.answer });
  });

  if (cards.length === 0) {
    return null;
  }

  return {
    sessionId: session.id,
    finishedAt: session.finishedAt,
    filteredLevels: session.filteredLevels,
    shuffled: session.shuffled,
    cards,
    answers
  };
};

export const getLatestTestResult = async (): Promise<SavedTestResult | null> => {
  const session = await db.testSessions.orderBy("finishedAt").reverse().first();
  if (!session) {
    return null;
  }
  return hydrateSession(session);
};

export const getTestResult = async (sessionId: string): Promise<SavedTestResult | null> => {
  const session = await db.testSessions.get(sessionId);
  if (!session) {
    return null;
  }
  return hydrateSession(session);
};

export const getTestHistory = async (limit = 20): Promise<TestSessionRecord[]> =>
  db.testSessions.orderBy("finishedAt").reverse().limit(limit).toArray();

export const getProgressOverview = async (): Promise<ProgressOverview> => {
  const [sessions, stats] = await Promise.all([
    db.testSessions.orderBy("finishedAt").toArray(),
    db.cardStats.toArray()
  ]);
  const history = [...sessions].reverse().slice(0, 20);

  return {
    testCount: sessions.length,
    accuracyPercent: overallAccuracyPercent(sessions),
    cardsSeen: stats.length,
    scores: sessionPercents(sessions),
    needsWork: rankNeedsWork(stats, getCardsById()),
    history
  };
};

export const getCardStat = (cardId: string) => db.cardStats.get(cardId);
